import { translationPacket } from 'common/actions';
import { Column, Row } from 'common/components/common/style';
import MetadataSeparator from 'common/components/metadata-strip/MetadataSeparator';
import { ClampContainer, H2, H3, P1 } from 'common/components/Typography';
import { Epg } from 'common/constants/data-types';
import { formatTime } from 'common/services/helpers';
import { formatStringToEllipsis, isTextOverflowing } from 'common/utils';
import React, { CSSProperties, useCallback, useEffect, useMemo, useRef } from 'react';
import { connect } from 'react-redux';
import { EpgShowInfoContainer, EpgShowType } from './Controls.styles';
import { getEpisodeInfoText } from './utils';

interface EpgShowInfoProps {
  // Redux injected
  i18n: typeof translationPacket;
  //
  type: EpgShowType;
  start: number;
  stop: number;
  title: string;
  major_category?: Epg['major_category'];
  minor_categories?: Epg['minor_categories'];
  numeration?: Epg['numeration'];
}

function EpgShowInfo(props: EpgShowInfoProps) {
  const episodeInfoText = useMemo(() => getEpisodeInfoText(props.numeration), [props.numeration]);
  const titleRef = useRef<HTMLElement | null>(null);
  const titleLines = useMemo(() => {
    return episodeInfoText ? 1 : 2;
  }, [episodeInfoText]);

  const [title, setTitle] = React.useState('');

  const getTitle = (ref: HTMLElement | null) => {
    if (!ref) return;
    titleRef.current = ref;
    setTitle(formatStringToEllipsis(titleRef.current, props.title, titleLines));
  };

  useEffect(() => {
    if (titleRef.current) {
      setTitle(formatStringToEllipsis(titleRef.current, props.title, titleLines));
    }
  }, [titleLines, props.title]);

  const titleOverflows = useMemo(
    () => titleRef.current && isTextOverflowing(titleRef.current, title, titleLines),
    [title, props.title]
  );

  const genreLines = titleOverflows || episodeInfoText ? 1 : 2;

  const inlineStyle = useMemo<CSSProperties>(
    () => ({
      textAlign: props.type === 'prev' ? 'left' : 'right'
    }),
    [props.type, titleLines]
  );

  const renderSubtitle = useCallback(() => {
    const before = props.type === 'prev' ? props.i18n.common.before : undefined;
    const upcoming = props.type === 'next' ? props.i18n.common.upcoming : undefined;
    const startTime = formatTime(props.start);
    const endTime = formatTime(props.stop);
    return (
      <Row>
        <P1>
          {before}
          {before && <MetadataSeparator spacing={8} />}
          {`${startTime} - ${endTime}`}
          {upcoming && <MetadataSeparator spacing={8} />}
          {upcoming}
        </P1>
      </Row>
    );
  }, [props.start, props.stop, props.type, props.i18n]);

  const renderEpisodeInfo = () => {
    return episodeInfoText && <H3 key="episode-info">{episodeInfoText}</H3>;
  };

  const renderMinorCategories = useCallback(() => {
    return [props.major_category || '', props.minor_categories].join(' · ');
  }, [props.major_category, props.minor_categories]);

  return (
    <Column>
      {
        <EpgShowInfoContainer type={props.type} lines={titleLines}>
          {renderSubtitle()}
          <H2>
            <ClampContainer
              ref={getTitle}
              lines={titleLines}
              wordBreak={titleLines === 1 ? 'break-all' : 'break-word'}
            >
              {title}
            </ClampContainer>
          </H2>
          {renderEpisodeInfo()}
          <P1 style={inlineStyle}>
            <ClampContainer lines={genreLines} wordBreak="break-all">
              {renderMinorCategories()}
            </ClampContainer>
          </P1>
        </EpgShowInfoContainer>
      }
    </Column>
  );
}

const mapStateToProps = ({ i18n }: any) => ({
  i18n
});

export default React.memo(connect(mapStateToProps, {})(EpgShowInfo));
